import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const objects = [
    { name: "Tree", thin: "tree-thin.png", thick: "tree-thick.png" },
    { name: "Book", thin: "book-thin.png", thick: "book-thick.png" },
    { name: "Pencil", thin: "pencil-thin.png", thick: "pencil-thick.png" },
    { name: "Candle", thin: "candle-thin.png", thick: "candle-thick.png" },
    { name: "Eggplant", thin: "eggplant-thin.png", thick: "eggplant-thick.png" },
    { name: "Loaf", thin: "loaf-thin.png", thick: "loaf-thick.png" },
    { name: "Log", thin: "log-thin.png", thick: "log-thick.png" },
    { name: "Paint Brush", thin: "paintbrush-thin.png", thick: "paintbrush-thick.png" },
];

const ThinThick: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [target, setTarget] = useState("long");
    const [selectedObject, setSelectedObject] = useState(objects[0]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Thin and Thick Worksheets for UKG | Visual Comparison Concepts";
    }, []);

    const generateQuestion = () => {
        const randomObject = objects[Math.floor(Math.random() * objects.length)];
        const randomTarget = Math.random() > 0.5 ? "thin" : "thick";

        setSelectedObject(randomObject);
        setTarget(randomTarget);
        setQuestion(`Which one is ${randomTarget}?`);
        setMessage("");
    };

    const checkAnswer = (choice: string) => {
        if (choice === target) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion();
                setShowImage(false);
            }, 3000);
        } else {
            setShowImage(false);
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG children the difference between thin and thick through interactive activities. Download free printable worksheets and help kids compare objects visually." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/thin-thick" />
            </Helmet>
            {showImage === false ? (
                <div className="contentPart matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Thin and Thick</h1>

                            <p>
                                The concept of <strong>thin</strong> and <strong>thick</strong> is a key part of <strong>pre-math education</strong> for UKG learners. By comparing widths visually, children start building observation skills and understanding measurable attributes in objects. Our <strong>thin and thick worksheets</strong> make this learning process fun and visual!
                            </p>

                            <h2>🧠 What Do Thin and Thick Mean?</h2>
                            <p>
                                <strong>Thin</strong> means narrow or not wide, while <strong>thick</strong> means broad or wide. Children learn to compare shapes and objects based on how narrow or wide they appear.
                            </p>

                            <ul>
                                <li>A pencil is <strong>thin</strong>, but a rolling pin is <strong>thick</strong>.</li>
                                <li>A thread is <strong>thinner</strong> than a rope.</li>
                                <li>A book is <strong>thicker</strong> than a sheet of paper.</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify and describe thin and thick objects</li>
                                <li>Visually compare the width of objects</li>
                                <li>Enhance vocabulary: thin, thick, thinner, thicker</li>
                                <li>Build visual perception and categorization skills</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Tick the thick object among two or three</li>
                                <li>Match thin and thick versions of the same item</li>
                                <li>Color the thinner object</li>
                                <li>Sort objects into thin and thick groups</li>
                                <li>Trace thick and thin lines for motor development</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Use daily objects like markers, sticks, books, and straws to show differences</li>
                                <li>Ask comparison questions: “Which crayon is thicker?”</li>
                                <li>Involve kids in fun sorting activities using kitchen or craft items</li>
                                <li>Let kids feel and describe items to reinforce understanding</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Long - Short</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Heavy - Light</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Front - Back</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Stack books, sticks, or paper sheets to let your child see and feel the difference between thick and thin. Talk about it as they explore.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Thin and Thick</h1>

                        <p>
                            The concept of <strong>thin</strong> and <strong>thick</strong> is a key part of <strong>pre-math education</strong> for UKG learners. By comparing widths visually, children start building observation skills and understanding measurable attributes in objects. Our <strong>thin and thick worksheets</strong> make this learning process fun and visual!
                        </p>

                        <h2>🧠 What Do Thin and Thick Mean?</h2>
                        <p>
                            <strong>Thin</strong> means narrow or not wide, while <strong>thick</strong> means broad or wide. Children learn to compare shapes and objects based on how narrow or wide they appear.
                        </p>

                        <ul>
                            <li>A pencil is <strong>thin</strong>, but a rolling pin is <strong>thick</strong>.</li>
                            <li>A thread is <strong>thinner</strong> than a rope.</li>
                            <li>A book is <strong>thicker</strong> than a sheet of paper.</li>
                        </ul>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify and describe thin and thick objects</li>
                            <li>Visually compare the width of objects</li>
                            <li>Enhance vocabulary: thin, thick, thinner, thicker</li>
                            <li>Build visual perception and categorization skills</li>
                        </ul>

                        <h2>🖍️ Activities in the Worksheets</h2>
                        <ul>
                            <li>Tick the thick object among two or three</li>
                            <li>Match thin and thick versions of the same item</li>
                            <li>Color the thinner object</li>
                            <li>Sort objects into thin and thick groups</li>
                            <li>Trace thick and thin lines for motor development</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Use daily objects like markers, sticks, books, and straws to show differences</li>
                            <li>Ask comparison questions: “Which crayon is thicker?”</li>
                            <li>Involve kids in fun sorting activities using kitchen or craft items</li>
                            <li>Let kids feel and describe items to reinforce understanding</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Long - Short</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Heavy - Light</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Front - Back</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Stack books, sticks, or paper sheets to let your child see and feel the difference between thick and thin. Talk about it as they explore.
                        </p>
                    </article>
                    <h2>{question}</h2>
                    <div className="mt-5 mb-5" style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                        <img src={process.env.REACT_APP_API_URL + selectedObject.thin} alt="Thin Object" style={{ width: "100px" }} onClick={() => checkAnswer("thin")} />
                        <img src={process.env.REACT_APP_API_URL + selectedObject.thick} alt="Thick Object" style={{ width: "100px" }} onClick={() => checkAnswer("thick")} />
                    </div>
                    <p>{message}</p>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_WELDONE_LINK} alt="Well Done" />
                </div>
            )}
        </>
    );
};

export default ThinThick;
